// TODO(dev:dashboard_v2, translations): Add missing translations
import {
  Card,
  CardContent,
  CardTitle,
  CardTitleSuffix,
  CardTitleActions,
} from "~/react/ui/card"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { PageHeader, PageHeaderTitle } from "~/react/ui/page-header"
import { HeadlineMetrics } from "./headline-metrics"
import { Skeleton } from "../ui/skeleton"
import { OpportunitySignalItem } from "./opportunity-signal-item"
import { EventType, LeadSignalItem } from "./lead-signal-item"
import { Separator } from "../ui/separator"
import { DashboardProvider } from "~/react/components/dashboard-provider"
import {
  DashboardTeamActivity,
  DashboardUserLookalike,
} from "@/types/serializers"

const seriesMapping: Record<string, number> = {
  "Pre-Seed": 0,
  Seed: 1,
  "Pre-Series A": 2,
  "Series A": 3,
  "Pre-Series B": 4,
  "Series B": 5,
  "Pre-Series C": 6,
  "Series C": 7,
  "Pre-Series D": 8,
  "Series D": 9,
  "Pre-Series E": 10,
  "Series E": 11,
  "Pre-Series F": 12,
  "Series F": 13,
  "Pre-Series G": 14,
  "Series G": 15,
  "Pre-Series H": 16,
  "Series H": 17,
  "Pre-Series I": 18,
  "Series I": 19,
}
const getSmallestFundingRound = (
  activities: DashboardTeamActivity[],
): string | null => {
  const smallestFundingRoundIndex = activities.reduce((minIndex, activity) => {
    const fundingRoundIndex =
      seriesMapping[activity.activity.associated_record_data.type] ?? 999
    return fundingRoundIndex < minIndex ? fundingRoundIndex : minIndex
  }, 999)

  if (smallestFundingRoundIndex === 999) {
    return null
  }

  return (
    Object.keys(seriesMapping).find(
      (key) => seriesMapping[key] === smallestFundingRoundIndex,
    ) ?? null
  )
}

const teamActivitiesToLeadSignals = (
  teamActivities: DashboardTeamActivity[],
) => {
  const groupedTeamActivities = teamActivities?.reduce<
    Record<string, DashboardTeamActivity[]>
  >((acc, activity) => {
    const activityDate = new Date(activity.created_at)
    const dateHour = `${activityDate.toLocaleDateString()} ${activityDate.getHours()}:00`
    const eventType = activity.activity.event_type
    const groupKey = `${dateHour}|${eventType}`

    if (!acc[groupKey]) {
      acc[groupKey] = []
    }
    acc[groupKey].push(activity)
    return acc
  }, {})

  const leadSignals = Object.keys(groupedTeamActivities)
    .map((key) => {
      const [dateHour, eventType] = key.split("|")
      const activities = groupedTeamActivities[key]
      const entityType = activities[0].activity.activity_type
      const entityCount = activities.length

      // Multiple activities of the same type in the same hour grouped together
      if (activities.length > 1) {
        let signal = ""
        switch (eventType as EventType) {
          case "email_added":
            signal = `${entityCount} companies on your target list have new email addresses`
            break
          case "technology_added":
            signal = `${entityCount} companies on your target list have added new technologies`
            break
          case "linkedin_job_added":
            signal = `${entityCount} companies on your target list have published new job openings`
            break
          case "funding_added":
            const smallestFundingRound = getSmallestFundingRound(activities)

            if (smallestFundingRound === null) {
              signal = `${entityCount} companies on your target list raised funding rounds`
            } else {
              signal = `${entityCount} companies on your target list raised ${smallestFundingRound} or later funding rounds`
            }
            break
          case "technology_deleted":
            signal = `${entityCount} companies on your target list have removed technologies`
            break
          case "executive_added":
            signal = `${entityCount} companies on your target list have added new executives`
            break
          case "spike_metric":
            signal = `${entityCount} companies on your target list have spiked metrics`
            break
          case "position_changed":
            signal = `${entityCount} companies on your target list have changed positions`
            break
          case "location_changed":
            signal = `${entityCount} companies on your target list have changed locations`
            break
        }

        if (signal === "") {
          return null
        }

        return {
          id: key,
          timestamp: new Date(dateHour),
          signal,
          eventType: eventType,
          entityCount,
          entityType: entityType,
          visited: false,
        }
      } else {
        const activity = activities[0]
        let signal = ""

        switch (eventType as EventType) {
          case "email_added":
            signal = `${activity.activity.associated_record_data?.company_name} has new email addresses`
            break
          case "technology_added":
            signal = `${activity.activity.associated_record_data?.company_name} has added new technologies`
            break
          case "linkedin_job_added":
            signal = `${activity.activity.associated_record_data?.company_name} has published new job openings`
            break
          case "funding_added":
            signal = `${activity.activity.associated_record_data?.company_name} has raised ${activity.activity.associated_record_data?.type} funding round`
            break
          case "technology_deleted":
            signal = `${activity.activity.associated_record_data?.company_name} has removed technologies`
            break
          case "executive_added":
            signal = `${activity.activity.associated_record_data?.company_name} has added new executives`
            break
          case "spike_metric":
            signal = `${activity.activity.associated_record_data?.company_name} has spiked metrics`
            break
          case "position_changed":
            signal = `${activity.activity.associated_record_data?.full_email} has changed position`
            break
          case "location_changed":
            signal = `${activity.activity.associated_record_data?.full_email} has changed location`
            break
        }

        if (signal === "") {
          return null
        }

        return {
          id: key,
          timestamp: new Date(dateHour),
          signal,
          eventType: eventType,
          entityCount: 1, // Single activity
          entityType: entityType,
          visited: false,
        }
      }
    })
    .filter((signal) => signal !== null)

  return leadSignals
}

interface DashboardProps {
  user?: {
    firstName?: string | null
    email?: string
  }
  numberOfCreditsLeft: number
  campaigns?: { id: number; name: string }[]
  leads_lists?: { id: number; name: string }[]
  company_lists?: { id: number; name: string }[]
  leads_count?: number
  leads_without_email?: number
  verified_leads?: number
  unread_inbox_messages?: number
  team_activities?: DashboardTeamActivity[]
  user_lookalikes?: DashboardUserLookalike[]
}

export default function Dashboard({
  user,
  numberOfCreditsLeft,
  campaigns = [],
  leads_lists = [],
  company_lists = [],
  leads_count = 0,
  leads_without_email = 0,
  verified_leads = 0,
  unread_inbox_messages = 0,
  team_activities = [],
  user_lookalikes = [],
}: DashboardProps) {
  const userName = user?.firstName || user?.email || "there"
  const leadSignals = teamActivitiesToLeadSignals(team_activities)

  return (
    <DashboardProvider
      initialCampaigns={campaigns}
      initialLeadsLists={leads_lists}
      initialCompanyLists={company_lists}
    >
      <div className="dashboard-container">
        <PageHeader variant="ghost">
          <PageHeaderTitle>Dashboard</PageHeaderTitle>
        </PageHeader>

        <div className="dashboard-v2__content">
          <p className="text-2xl-medium">Hey, {userName}!</p>

          <div className="dashboard-v2__layout">
            <div className="dashboard-v2__main-column">
              <Card className="dashboard-v2__overview">
                <CardContent>
                  <HeadlineMetrics
                    leads_count={leads_count}
                    leads_without_email={leads_without_email}
                    verified_leads={verified_leads}
                    unread_inbox_messages={unread_inbox_messages}
                  />
                </CardContent>
              </Card>

              <div className="dashboard-v2__signals">
                <Card
                  className="dashboard-v2__opportunity-signals"
                  grow
                  density="compact"
                  outerTitle={
                    <CardTitle>
                      Opportunity signals
                      <CardTitleSuffix>
                        {user_lookalikes.length.toString()}
                      </CardTitleSuffix>
                      <CardTitleActions>
                        <Button
                          variant="ghost"
                          size="xs"
                          iconOnly
                          title="Configure"
                          aria-label="Configure"
                        >
                          <Icon icon="cog" aria-hidden="true" size="sm" />
                        </Button>
                      </CardTitleActions>
                    </CardTitle>
                  }
                >
                  <CardContent>
                    {user_lookalikes.length === 0 && (
                      <div className="text-md-medium d-flex justify-center items-center p-4">
                        No opportunity signals found
                      </div>
                    )}
                    {user_lookalikes.map((lookalike, index) => (
                      <>
                        <OpportunitySignalItem
                          key={lookalike.id}
                          id={lookalike.id}
                          title={lookalike.lookalike_type}
                          visited={false}
                          description={lookalike.cluster_description}
                          numberOfCreditsLeft={numberOfCreditsLeft}
                          type={"hunter-suggestion"}
                          entityType={lookalike.lookalike_type}
                          entityCount={lookalike.count_of_results}
                          lookalikeIds={lookalike.lookalike_ids}
                        />
                        {index < user_lookalikes.length - 1 && (
                          <Separator variant="light" />
                        )}
                      </>
                    ))}
                  </CardContent>
                </Card>

                <Card
                  className="dashboard-v2__lead-signals"
                  grow
                  density="compact"
                  outerTitle={
                    <CardTitle>
                      Lead signals
                      <CardTitleActions>
                        <Button
                          variant="ghost"
                          size="xs"
                          iconOnly
                          title="How it works"
                          aria-label="How it works"
                        >
                          <Icon
                            icon="question-circle"
                            aria-hidden="true"
                            size="sm"
                          />
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          iconOnly
                          title="Configure"
                          aria-label="Configure"
                        >
                          <Icon icon="cog" aria-hidden="true" size="sm" />
                        </Button>
                      </CardTitleActions>
                    </CardTitle>
                  }
                >
                  <CardContent>
                    {leadSignals.map((signal, index) => (
                      <>
                        <LeadSignalItem
                          key={signal.id}
                          message={signal.signal}
                          timestamp={signal.timestamp}
                          eventType={signal.eventType}
                          entityType={signal.entityType}
                          count={signal.count}
                          visited={signal.visited}
                        />
                        {index < leadSignals.length - 1 && (
                          <Separator variant="light" />
                        )}
                      </>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <div className="dashboard-v2__useful-resources">
                <span className="text-md-medium">Useful resources</span>
                <div className="dashboard-v2__useful-resources-list">
                  <Card
                    variant="ghost"
                    isLink
                    href="/connected-apps?tab=browser-extensions"
                    linkAriaLabel="Go to Browser Extension"
                  >
                    <CardContent>
                      <h3 className="dashboard-v2__resource-title">
                        <Icon icon="chrome" variant="brand" size="sm" />
                        <span>Browser Extension</span>
                      </h3>
                      <p className="dashboard-v2__resource-description">
                        Find email addresses while you're browsing the web.
                      </p>
                    </CardContent>
                  </Card>

                  <Card
                    variant="ghost"
                    isLink
                    href="/webinars"
                    linkAriaLabel="Go to Upcoming webinars"
                  >
                    <CardContent>
                      <h3 className="dashboard-v2__resource-title">
                        <Icon icon="video" size="sm" />
                        <span>Upcoming webinars</span>
                      </h3>
                      <p className="dashboard-v2__resource-description">
                        Level up your cold email knowledge Q&A sessions.
                      </p>
                    </CardContent>
                  </Card>

                  <Card
                    variant="ghost"
                    isLink
                    href="https://hunter.io/cold-email-guide/"
                    linkAriaLabel="Go to Cold Email Guide"
                  >
                    <CardContent>
                      <h3 className="dashboard-v2__resource-title">
                        <Icon icon="book" size="sm" />
                        <span>Cold Email Guide</span>
                      </h3>
                      <p className="dashboard-v2__resource-description">
                        Your guide to sending effective email outreach.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            <div className="dashboard-v2__side-column">
              <Card className="dashboard-v2__unknown-1" grow>
                <CardContent>
                  <div className="d-flex flex-column gap-4">
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                  </div>
                </CardContent>
              </Card>

              <Card className="dashboard-v2__unknown-2" grow>
                <CardContent>
                  <div className="d-flex flex-column gap-4">
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                  </div>
                </CardContent>
              </Card>

              <Card className="dashboard-v2__unknown-3" grow>
                <CardContent>
                  <div className="d-flex flex-column gap-4">
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                    <Skeleton noAnimate />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </DashboardProvider>
  )
}
